import {Link} from '@app/dashboard/links/link';
import {Navbar} from '@common/ui/navigation/navbar/navbar';
import {CustomPageBody} from '@common/custom-page/custom-page-body';
import {Footer} from '@common/ui/footer/footer';
import {ShareLinkButton} from '@app/dashboard/links/sharing/share-link-button';
import {AdHost} from '@common/admin/ads/ad-host';
import {RedirectCountdownButton} from '@app/short-links/renderers/redirect-countdown-button';
import {useEffect, useMemo, useState} from 'react';
import clsx from 'clsx';

// Extend local (sin tocar types globales todavía)
type LinkWithVisitConfig = Link & {
  visitConfig?: {
    adsEnabled?: boolean;
    tier?: string;
    reason?: string;
  };
  visit_config?: {
    adsEnabled?: boolean;
    ads_enabled?: boolean;
    tier?: string;
    reason?: string;
  };
};

interface LinkPageRendererProps {
  link: Link;
}

export function LinkPageRenderer({link}: LinkPageRendererProps) {
  const l = link as LinkWithVisitConfig;

  const adsEnabledFromBackend =
    l.visitConfig?.adsEnabled ??
    l.visit_config?.adsEnabled ??
    l.visit_config?.ads_enabled ??
    undefined;

  // default: mantiene el comportamiento anterior (ads ON) si backend no manda nada
  const adsEnabled =
    typeof adsEnabledFromBackend === 'boolean' ? adsEnabledFromBackend : true;

  const page = link.custom_page!;
  const {hideNavbar, hideFooter} = page.meta || {};

  // ---------- Preview image (screenshot) ----------
  const previewSrc = useMemo(() => `/${link.hash}/img`, [link.hash]);

  // estados de carga (mostramos spinner mientras el backend genera o hasta que haya imagen)
  const [imgReady, setImgReady] = useState(false);
  const [imgErrored, setImgErrored] = useState(false);

  // si cambia el link, reseteamos
  useEffect(() => {
    setImgReady(false);
    setImgErrored(false);
  }, [previewSrc]);

  // cuando falla el <img> (404 => backend aún no tiene screenshot), reintentamos cada X ms
  useEffect(() => {
    if (!imgErrored || imgReady) return;

    const id = window.setInterval(() => {
      // “cache buster” para forzar un request nuevo
      const bust = Date.now();
      const img = new Image();
      img.onload = () => {
        setImgReady(true);
        setImgErrored(false);
      };
      img.onerror = () => {
        // seguimos en modo spinner
      };
      img.src = `${previewSrc}?t=${bust}`;
    }, 1500);

    return () => window.clearInterval(id);
  }, [imgErrored, imgReady, previewSrc]);

  return (
    <div className="flex flex-col min-h-screen">
      {!hideNavbar && <LinkPageNavbar link={link} />}

      {adsEnabled ? <AdHost slot="link_page" className="mt-70 mb-20" /> : null}

      {/* Preview (spinner mientras llega la imagen) */}
      <div className="mx-auto w-full max-w-5xl px-24 mt-20">
        <div className="relative overflow-hidden rounded border bg-paper">
          {/* spinner */}
          {!imgReady ? (
            <div className="flex items-center justify-center h-220">
              {/* “spinner bootstrap-like” usando tailwind */}
              <div
                className={clsx(
                  'h-36 w-36 rounded-full border-4 border-muted/30 border-t-primary animate-spin',
                )}
                aria-label="Loading preview"
              />
            </div>
          ) : null}

          {/* imagen real (la dejamos siempre en DOM para que dispare onLoad/onError) */}
          <img
            src={`${previewSrc}?t=${Date.now()}`}
            alt=""
            className={clsx(
              'w-full h-220 object-cover',
              imgReady ? 'block' : 'hidden',
            )}
            loading="eager"
            onLoad={() => {
              setImgReady(true);
              setImgErrored(false);
            }}
            onError={() => {
              // 404 => aún no existe, mostramos spinner + reintentos
              setImgReady(false);
              setImgErrored(true);
            }}
          />
        </div>
      </div>

      <div className="flex-auto">
        <CustomPageBody page={page} />
      </div>

      {!hideFooter && <Footer className="mx-14 md:mx-40" />}
    </div>
  );
}

interface LinkPageNavbarProps {
  link: Link;
}

function LinkPageNavbar({link}: LinkPageNavbarProps) {
  return (
    <Navbar
      menuPosition="link-page-navbar"
      className="flex-shrink-0 sticky top-0"
      rightChildren={<ShareLinkButton link={link} />}
    >
      <RedirectCountdownButton variant="flat" color="paper" link={link} />
    </Navbar>
  );
}
